import { ActionButton } from '@react-spectrum/s2';
import ChevronLeft from '@react-spectrum/s2/icons/ChevronLeft';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Button as RACButton } from 'react-aria-components';

import {
	getFrameRootCapPx,
	getSocialEmbed,
} from '@/components/social/socialEmbed.ts';
import { cn } from '@/utils/cn.ts';

import type { PanelArtifact } from '../../../../../bridge/types/messages.ts';

import { SocialPostPreview } from './SocialPostPreview.tsx';

// The tile is border-box, and glass-card-sm plus p-3 spends 26px per axis. The
// rest buys the two 12px track gaps and 24px of peek on each side, so a
// neighbour always shows exactly 24px whichever member is in the middle.
const TILE_ALLOWANCE_PX = 98;
// The same allowance without the tile chrome. Subtracting it inside the slide
// width hands the whole peek back, so the active card keeps its full cap.
const SLIDE_ALLOWANCE_PX = 72;

// What an S2 ActionButton hands back through a ref. Its own focus() takes no
// options, and the chat column scrolls, so the rescue needs the DOM node to pass
// preventScroll.
type ChevronHandle = {
	focus(): void;
	UNSAFE_getDOMNode(): HTMLButtonElement | null;
};

const prefersReducedMotion = (): boolean =>
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The arrow keys belong to whatever the customer is typing in, and to any widget
// that navigates with them. The set only takes them when nothing else wants them.
const isKeyConsumer = (node: Element | null): boolean => {
	if (!(node instanceof HTMLElement)) return false;
	if (node.isContentEditable) return true;
	const tag = node.tagName;
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
	return (
		node.closest(
			'[role="listbox"],[role="grid"],[role="radiogroup"],[role="tablist"]',
		) !== null
	);
};

/**
 * The saved posts from one customer message, as one set to move through.
 *
 * Several links in one message would otherwise stack a full player per link and
 * push the conversation off the screen. The set shows one post at a time, dims
 * the neighbours at the edges, and states which post the customer is on.
 */
export const SocialPostSet = ({
	messages,
	isFresh,
}: {
	messages: PanelArtifact[];
	/** False once the customer has sent another message. The offer disappears and
	 * nothing starts playing, so a re-render cannot begin audio in a
	 * conversation that has moved on. */
	isFresh: boolean;
}) => {
	const [index, setIndex] = useState(0);
	const [hasNavigated, setHasNavigated] = useState(false);
	const setRootRef = useRef<HTMLDivElement>(null);
	const viewportRef = useRef<HTMLDivElement>(null);
	const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
	const prevRef = useRef<ChevronHandle | null>(null);
	const nextRef = useRef<ChevronHandle | null>(null);
	// A ref, never state. A re-render on pointer-enter inside a container-type
	// box makes Blink reset the nearest scrolling ancestor to the top, and this
	// set lives inside the chat scroller. That is ADO-1309.
	const isViewportHoveredRef = useRef(false);
	const focusWasInSetRef = useRef(false);
	const hasPositionedRef = useRef(false);

	const count = messages.length;
	// One derived boolean, and the ONLY dependency of the wheel listener. A
	// dependency on the member count would rebuild the listener as each member
	// arrives, and the cooldown lives in that listener's own scope.
	const isSet = count >= 2;
	const safeIndex = Math.min(Math.max(index, 0), Math.max(count - 1, 0));

	// Each member's OWN cap, in member order. The slide below hands this to the
	// body that holds the card, so a narrower post keeps its own width inside a
	// wider slide.
	const capsPx = useMemo(
		() =>
			messages.map((message) =>
				getFrameRootCapPx(getSocialEmbed(message.source_post?.url ?? '')),
			),
		[messages],
	);

	// Every slide takes the SAME width, from the widest member. The centring
	// formula splits the leftover room evenly around the active slide, so a
	// per-member width would make the peek change every time the customer moves.
	const widestCapPx = useMemo(
		() => capsPx.reduce((widest, cap) => Math.max(widest, cap), 0),
		[capsPx],
	);

	const moveTo = useCallback(
		(raw: number) => {
			const next = Math.min(Math.max(raw, 0), count - 1);
			if (next === safeIndex) return;
			// Capture BEFORE the state change. Once React commits, the browser has
			// already blurred a node that unmounted, went inert, or became
			// disabled, so a later read always answers the body element.
			focusWasInSetRef.current =
				setRootRef.current?.contains(document.activeElement) ?? false;
			setHasNavigated(true);
			setIndex(next);
		},
		[count, safeIndex],
	);

	const goPrev = useCallback(() => moveTo(safeIndex - 1), [moveTo, safeIndex]);
	const goNext = useCallback(() => moveTo(safeIndex + 1), [moveTo, safeIndex]);

	const goPrevRef = useRef(goPrev);
	const goNextRef = useRef(goNext);
	goPrevRef.current = goPrev;
	goNextRef.current = goNext;

	const centerActiveSlide = useCallback(
		(behavior: ScrollBehavior) => {
			const viewport = viewportRef.current;
			const slide = slideRefs.current[safeIndex];
			if (!viewport || !slide) return;
			const target =
				slide.offsetLeft - (viewport.clientWidth - slide.clientWidth) / 2;
			// Read the setting here, not at mount, so a customer who turns it on
			// mid-conversation is honoured. Never pass 'auto': the CSS definition of
			// 'auto' is the element's own scroll-behavior, and this viewport is
			// scroll-smooth, so 'auto' would still animate.
			const honest =
				behavior === 'smooth' && prefersReducedMotion() ? 'instant' : behavior;
			viewport.scrollTo({ left: Math.max(0, target), behavior: honest });
		},
		[safeIndex],
	);

	useLayoutEffect(() => {
		const root = setRootRef.current;
		if (focusWasInSetRef.current) {
			focusWasInSetRef.current = false;
			// Force the pending style update first. The browser clears focus from a
			// node that entered an inert subtree during that update, so a check
			// before it would still see the old focus and skip the rescue.
			void root?.offsetWidth;
			if (root && !root.contains(document.activeElement)) {
				// By state, never by position: the control the customer just pressed
				// may be the one this change disabled.
				const target =
					safeIndex < count - 1 ? nextRef.current : prevRef.current;
				target?.UNSAFE_getDOMNode()?.focus({ preventScroll: true });
			}
		}
		centerActiveSlide(hasPositionedRef.current ? 'smooth' : 'instant');
		hasPositionedRef.current = true;
	}, [centerActiveSlide, safeIndex, count]);

	// The chat column re-flexes through React state when a panel opens, and that
	// fires no window event, so only an observer notices the stage changing width.
	useEffect(() => {
		const viewport = viewportRef.current;
		const slide = slideRefs.current[safeIndex];
		if (!viewport) return;
		let firstCall = true;
		const observer = new ResizeObserver(() => {
			if (firstCall) {
				firstCall = false;
				return;
			}
			centerActiveSlide('instant');
		});
		observer.observe(viewport);
		if (slide) observer.observe(slide);
		return () => observer.disconnect();
	}, [safeIndex, centerActiveSlide]);

	useEffect(() => {
		if (!isSet) return;
		const handler = (event: KeyboardEvent) => {
			const active = document.activeElement;
			const focusInSet = setRootRef.current?.contains(active) ?? false;
			// Focus outranks hover, so exactly one set answers a key press. The
			// listener is on the window, and hover and focus are independent, so
			// focus in set A with the pointer resting over set B passed BOTH sets'
			// guards and moved both on one ArrowRight.
			const focusedSet = active?.closest?.('[data-testid="social-post-set"]');
			if (focusedSet && focusedSet !== setRootRef.current) return;
			// The pointer resting over the set, or the customer's focus inside it.
			// A keyboard-only customer never hovers, so focus has to count too.
			if (!isViewportHoveredRef.current && !focusInSet) return;
			if (isKeyConsumer(active)) return;
			// A MODIFIED arrow key belongs to the browser, never to the carousel.
			// Alt+ArrowLeft is Back on Windows and Linux, Command+ArrowLeft is Back
			// on macOS, and the handler below cancels the default before it moves.
			// So resting the pointer over a set took the customer's Back and
			// Forward away and moved the carousel instead.
			if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey)
				return;
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				goPrev();
			} else if (event.key === 'ArrowRight') {
				event.preventDefault();
				goNext();
			}
		};
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [isSet, goPrev, goNext]);

	useEffect(() => {
		if (!isSet) return;
		const viewport = viewportRef.current;
		if (!viewport) return;
		const COOLDOWN_MS = 700;
		const MIN_DELTA = 4;
		let cooldownUntil = 0;
		const handler = (event: WheelEvent) => {
			if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
			event.preventDefault();
			if (Math.abs(event.deltaX) < MIN_DELTA) return;
			const now = performance.now();
			if (now < cooldownUntil) return;
			// Through refs, so this listener attaches once. Members keep arriving
			// after the set forms, and the callbacks clamp on the member count.
			if (event.deltaX > 0) goNextRef.current();
			else goPrevRef.current();
			cooldownUntil = now + COOLDOWN_MS;
		};
		viewport.addEventListener('wheel', handler, { passive: false });
		return () => {
			viewport.removeEventListener('wheel', handler);
		};
	}, [isSet]);

	return (
		<div
			ref={setRootRef}
			className="glass-card-sm w-full p-3"
			style={{ maxWidth: widestCapPx + TILE_ALLOWANCE_PX }}
			role="group"
			aria-label={`${count} saved posts`}
			data-testid="social-post-set"
		>
			<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
				<span
					className="text-sm font-semibold text-(--text-primary)"
					data-testid="social-post-set-name"
				>
					{count} saved posts
				</span>
				<div className="flex items-center gap-1">
					<span
						className="text-xs text-(--text-muted) tabular-nums"
						data-testid="social-post-set-counter"
					>
						{safeIndex + 1} of {count}
					</span>
					<ActionButton
						ref={prevRef}
						isQuiet
						aria-label="Previous post"
						data-testid="social-post-set-prev"
						isDisabled={safeIndex === 0}
						onPress={goPrev}
					>
						<ChevronLeft />
					</ActionButton>
					<ActionButton
						ref={nextRef}
						isQuiet
						aria-label="Next post"
						data-testid="social-post-set-next"
						isDisabled={safeIndex >= count - 1}
						onPress={goNext}
					>
						<ChevronRight />
					</ActionButton>
				</div>
			</div>
			{/* Silent until the customer moves, so a set that is still filling does
			    not announce a position nobody asked for. */}
			<span role="status" aria-live="polite" className="sr-only">
				{hasNavigated ? `Post ${safeIndex + 1} of ${count}` : ''}
			</span>
			<div
				ref={viewportRef}
				data-testid="social-post-set-stage"
				onMouseEnter={() => {
					isViewportHoveredRef.current = true;
				}}
				onMouseLeave={() => {
					isViewportHoveredRef.current = false;
				}}
				className="[container-type:inline-size] relative w-full
					overflow-x-hidden overscroll-x-contain scroll-smooth
					motion-reduce:scroll-auto"
			>
				{/* items-start, never items-center. The stage is as tall as the tallest
			    member, and an Instagram embed is taller than a bare picture because
			    Instagram wraps its own header and like bar around it. Centred, a
			    shorter post floats in the middle of a tall box and reads as shrunken.
			    Aligned on one top edge, the posts read as a deck and the spare room
			    falls under the caption, where it reads as padding. */}
				<div className="flex items-start gap-3">
					<div
						aria-hidden="true"
						className="flex-shrink-0"
						style={{ width: '50cqw' }}
					/>
					{messages.map((message, i) => {
						const isActive = i === safeIndex;
						return (
							<div
								key={message.id}
								ref={(element) => {
									slideRefs.current[i] = element;
								}}
								data-testid={`panel-artifact-${message.id}`}
								className={cn(
									`relative flex w-(--social-set-slide-w) flex-shrink-0
									transition-opacity duration-300 motion-reduce:transition-none`,
									// Each slide takes the widest member's width, so a narrower post has
									// spare room. Push neighbours towards the middle, or that spare room
									// lands at the edge and the peek shows blank space, not the next post.
									isActive
										? 'justify-center'
										: i < safeIndex
											? 'justify-end'
											: 'justify-start',
									isActive ? 'opacity-100' : 'opacity-30 hover:opacity-50',
								)}
								style={{
									// Through a custom property rather than a plain width, so the
									// value survives a jsdom style object and a unit test can
									// assert the number. jsdom drops a width it cannot parse, and
									// it cannot parse cqw.
									['--social-set-slide-w' as string]: `min(100cqw - ${SLIDE_ALLOWANCE_PX}px, ${widestCapPx}px)`,
								}}
							>
								{/* inert takes the dimmed post's link, its play disc and its
								    offer out of the pointer and the tab order together. */}
								<div
									inert={!isActive}
									data-testid={`social-post-set-body-${message.id}`}
									// w-full is load-bearing. The card inside is w-full under a
									// max-width cap, and a percentage width needs a definite
									// parent. Without it this wrapper sizes to its own content as
									// a flex item, so the card never fills the slide and a tall
									// embed renders narrower than a short one in the same set.
									className={cn('w-full', !isActive && 'pointer-events-none')}
									// The cap is load-bearing too, and it carries THIS member's
									// width, not the set's. w-full alone makes this body fill the
									// whole slide, so the slide's justify-* class has no free room
									// and moves nothing. A 362px post then sits at the left edge of
									// a 586px slide, and 224px of blank space faces the middle.
									style={{ maxWidth: capsPx[i] }}
								>
									<SocialPostPreview
										message={message}
										sourceUrl={message.source_post?.url ?? ''}
										// Every member renders its offer, not just the one in view.
										// Gating it on the active post changed each card's height as
										// the customer moved, so the set grew and shrank and the
										// thread lost the customer's place. The dimmed body is inert,
										// so an offer rendered there is unreachable anyway.
										canBuildCarousel={isFresh}
										autoPlay={i === 0 && isFresh}
										paused={!isActive}
									/>
								</div>
								{!isActive && (
									// A sibling of the inert body, never its parent: a button
									// around the card would hold a link and a player inside a
									// button. Out of the tab order on purpose — focusing it would
									// make the browser scroll this hidden viewport and leave the
									// stage showing a post the counter does not name. The
									// chevrons are the keyboard route.
									<RACButton
										onPress={() => moveTo(i)}
										excludeFromTabOrder
										aria-label={`Move to post ${i + 1} of ${count}`}
										className="absolute inset-0 z-10 cursor-pointer
											bg-transparent outline-none data-[focus-visible]:outline-2
											data-[focus-visible]:-outline-offset-2
											data-[focus-visible]:outline-(--text-primary)"
									/>
								)}
							</div>
						);
					})}
					<div
						aria-hidden="true"
						className="flex-shrink-0"
						style={{ width: '50cqw' }}
					/>
				</div>
			</div>
		</div>
	);
};
